/**
 * VirtualCheckin — Check-in flow + NFT Room Key visualization
 */
import { useState } from 'react';
import { X, Loader2, KeyRound, CheckCircle2, Building2, Calendar, User, DoorOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function NFTRoomKey({ roomKey }) {
  return (
    <div className="relative" data-testid="nft-room-key">
      {/* Card */}
      <div className="relative w-64 mx-auto rounded-2xl overflow-hidden border border-[#F7931A]/30 bg-gradient-to-br from-[#1a1a2e] to-[#0a0e1a] p-5 shadow-[0_0_40px_rgba(247,147,26,0.1)]">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F7931A] to-transparent" />

        {/* BHERMES seal */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
              <KeyRound className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#F7931A]">BHERMES</p>
              <p className="text-[8px] text-gray-500">Digital Room Key</p>
            </div>
          </div>
          <div className="h-5 w-5 rounded-full border border-emerald-500/40 flex items-center justify-center animate-pulse">
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          </div>
        </div>

        {/* Room number */}
        <div className="text-center mb-4">
          <p className="text-[10px] text-gray-500 mb-0.5">Room</p>
          <p className="text-3xl font-bold text-white tracking-wider">{roomKey.room_number}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{roomKey.room_type}</p>
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-[10px]">
            <User className="h-3 w-3 text-gray-500" />
            <span className="text-gray-400">{roomKey.guest_name}</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <Building2 className="h-3 w-3 text-gray-500" />
            <span className="text-gray-400">{roomKey.hotel_name}</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <Calendar className="h-3 w-3 text-gray-500" />
            <span className="text-gray-400">{roomKey.check_in} {roomKey.check_out ? `→ ${roomKey.check_out}` : ''}</span>
          </div>
        </div>

        {/* NFT ID pattern */}
        <div className="bg-white/[0.02] rounded-lg p-2 border border-white/5">
          <div className="flex items-center justify-between text-[8px] text-gray-600 mb-1">
            <span>KEY ID</span>
            <span>VERIFIED</span>
          </div>
          <p className="text-[9px] text-gray-500 font-mono tracking-wider">{roomKey.key_id}</p>
          {/* Visual hash pattern */}
          <div className="flex gap-0.5 mt-1.5">
            {Array.from({ length: 16 }).map((_, i) => (
              <div
                key={i}
                className="h-2 flex-1 rounded-sm"
                style={{
                  backgroundColor: `rgba(247, 147, 26, ${0.15 + (Math.sin(i * 2.3 + roomKey.key_id.charCodeAt(i % roomKey.key_id.length)) * 0.5 + 0.5) * 0.5})`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom glow */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
      </div>
    </div>
  );
}

export default function VirtualCheckin({ hotelId, onClose }) {
  const [step, setStep] = useState('lookup'); // lookup | preview | success
  const [lookupType, setLookupType] = useState('booking_id');
  const [lookupValue, setLookupValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [roomKey, setRoomKey] = useState(null);

  const handleLookup = async () => {
    if (!lookupValue.trim()) return;
    setLoading(true);
    try {
      const body = { hotel_id: hotelId };
      if (lookupType === 'booking_id') body.booking_id = lookupValue.trim();
      else body.guest_email = lookupValue.trim();

      const res = await fetch(`${API}/api/metaverse/booking-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setBooking(data.booking);
        setStep('preview');
      } else {
        toast.error(data.error || 'Booking not found');
      }
    } catch {
      toast.error('Failed to look up booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metaverse/virtual-checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          booking_id: booking.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setRoomKey(data.room_key);
        setStep('success');
        toast.success(data.already_checked_in ? 'Welcome back! Here is your room key.' : 'Check-in successful! Your digital room key is ready.');
      } else {
        toast.error(data.error || 'Check-in failed');
      }
    } catch {
      toast.error('Failed to complete check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="virtual-checkin-overlay">
      <div className="bg-[#0c1020] border border-white/10 rounded-2xl w-[340px] max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="virtual-checkin-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-[#F7931A]/10 flex items-center justify-center">
              <DoorOpen className="h-4 w-4 text-[#F7931A]" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Virtual Check-in</p>
              <p className="text-[10px] text-gray-500">Metaverse Experience</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-white" onClick={onClose} data-testid="checkin-close-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4">
          {step === 'lookup' && (
            <div className="space-y-4" data-testid="checkin-lookup">
              <p className="text-xs text-gray-400">Enter your booking reference to check in virtually.</p>

              {/* Lookup type toggle */}
              <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
                <button
                  onClick={() => setLookupType('booking_id')}
                  className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors ${lookupType === 'booking_id' ? 'bg-[#F7931A] text-black font-medium' : 'text-gray-400'}`}
                  data-testid="lookup-type-id"
                >
                  Booking ID
                </button>
                <button
                  onClick={() => setLookupType('email')}
                  className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors ${lookupType === 'email' ? 'bg-[#F7931A] text-black font-medium' : 'text-gray-400'}`}
                  data-testid="lookup-type-email"
                >
                  Email
                </button>
              </div>

              <input
                value={lookupValue}
                onChange={e => setLookupValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder={lookupType === 'booking_id' ? 'e.g. BK-abc123' : 'guest@email.com'}
                className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#F7931A]/30"
                data-testid="checkin-input"
              />

              <Button
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black text-xs font-medium"
                onClick={handleLookup}
                disabled={loading || !lookupValue.trim()}
                data-testid="checkin-lookup-btn"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Look Up Booking
              </Button>
            </div>
          )}

          {step === 'preview' && booking && (
            <div className="space-y-4" data-testid="checkin-preview">
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-white">{booking.guest_name}</p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                    booking.is_checked_in
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-[#F7931A]/10 text-[#F7931A]'
                  }`}>
                    {booking.is_checked_in ? 'Checked In' : booking.status}
                  </span>
                </div>
                <div className="text-[10px] text-gray-500 space-y-1">
                  <p>Room: {booking.room_type}</p>
                  <p>Check-in: {booking.check_in}</p>
                  <p>Check-out: {booking.check_out}</p>
                  <p className="text-gray-600 font-mono">ID: {booking.id}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs border-white/10 text-gray-400"
                  onClick={() => { setStep('lookup'); setBooking(null); }}
                  data-testid="checkin-back-btn"
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-[#F7931A] hover:bg-[#F7931A]/90 text-black text-xs font-medium"
                  onClick={handleCheckin}
                  disabled={loading}
                  data-testid="checkin-confirm-btn"
                >
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
                  {booking.is_checked_in ? 'Get Room Key' : 'Check In Now'}
                </Button>
              </div>
            </div>
          )}

          {step === 'success' && roomKey && (
            <div className="space-y-4" data-testid="checkin-success">
              <div className="text-center mb-2">
                <div className="inline-flex items-center gap-1.5 text-emerald-400 text-xs mb-1">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium">Check-in Complete</span>
                </div>
                <p className="text-[10px] text-gray-500">Your NFT room key has been issued</p>
              </div>

              <NFTRoomKey roomKey={roomKey} />

              <Button
                className="w-full bg-white/5 hover:bg-white/10 text-gray-300 text-xs border border-white/5"
                onClick={onClose}
                data-testid="checkin-done-btn"
              >
                Continue Exploring
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
